import React, { useEffect, useMemo, useState } from "react";
import { App, Form } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import authService from "../../../services/auth.service";
import adminConsoleService from "../../../services/adminConsole.service";
import type {
  AdminConsoleMeta,
  PaginationResult,
  ProfileResponse,
  TrashResponse,
  User,
} from "../../../types";
import {
  PAGE_SIZE,
  type ListItem,
  type ModalMode,
  type SelectedRecord,
  type TabKey,
  type TrashRow,
} from "../constants";
import { buildEmptyForm, buildPayloadFromForm, normalizeRecordToForm } from "../utils";
import { ADMIN_ROUTES } from "../../../routes/config";

export function useAdminConsole() {
  const { message } = App.useApp();
  const location = useLocation();
  const navigate = useNavigate();

  const [form] = Form.useForm();
  const [profileForm] = Form.useForm();

  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [meta, setMeta] = useState<AdminConsoleMeta | null>(null);

  const tab = useMemo(() => {
    const found = ADMIN_ROUTES.find(r => r.path === location.pathname);
    return found ? found.tab : ("admins" as TabKey);
  }, [location.pathname]);

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [listData, setListData] = useState<PaginationResult<ListItem> | null>(null);
  const [trashData, setTrashData] = useState<TrashResponse | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<SelectedRecord>(null);
  const [error, setError] = useState("");
  const [creatingNew, setCreatingNew] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profile, setProfile] = useState<ProfileResponse | null>(null);

  const setTab = (key: TabKey) => {
    const route = ADMIN_ROUTES.find(r => r.tab === key);
    if (route) {
      navigate(route.path);
      setPage(1);
      setSearch("");
    }
  };

  const activeLabel = useMemo(
    () => ADMIN_ROUTES.find((item) => item.tab === tab)?.label || "Admin Console",
    [tab],
  );

  const domainOptions = useMemo(
    () =>
      (meta?.domains || []).map((domain) => ({
        label: domain.name || domain.code,
        value: domain.id || domain.code,
      })),
    [meta],
  );

  const backendRoleOptions = useMemo(
    () =>
      (meta?.backendRoles || []).map((role) => ({
        label: `${role.name}${role.level ? ` (L${role.level})` : ""}`,
        value: role.id,
      })),
    [meta],
  );

  const trashRows = useMemo<TrashRow[]>(() => {
    const users = (trashData?.users.items || []).map((item) => ({ ...item, type: "users" as const }));
    const admins = (trashData?.admins.items || []).map((item) => ({ ...item, type: "admins" as const }));
    const domains = (trashData?.domains.items || []).map((item) => ({ ...item, type: "domains" as const }));
    const roles = (trashData?.roles.items || []).map((item) => ({ ...item, type: "roles" as const }));
    return [...users, ...admins, ...domains, ...roles];
  }, [trashData]);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      try {
        setLoading(true);
        const validation = await authService.validateAdmin();
        if (!mounted) return;
        if (!validation.valid || !validation.user) {
          navigate("/login");
          return;
        }
        authService.saveUser(validation.user);
        setCurrentUser(validation.user);
        const adminMeta = await adminConsoleService.getMeta();
        if (mounted) setMeta(adminMeta);
      } catch (err: any) {
        if (mounted) setError(err.response?.data?.message || "Admin console could not be loaded.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void run();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    let mounted = true;
    const run = async () => {
      try {
        setLoading(true);
        setError("");
        if (tab === "trash") {
          const data = await adminConsoleService.getTrash({ page, limit: PAGE_SIZE, search });
          if (mounted) { setTrashData(data); setListData(null); }
          return;
        }
        const data =
          tab === "users" ? await adminConsoleService.getUsers({ page, limit: PAGE_SIZE, search })
            : tab === "admins" ? await adminConsoleService.getAdmins({ page, limit: PAGE_SIZE, search })
              : tab === "domains" ? await adminConsoleService.getDomains({ page, limit: PAGE_SIZE, search })
                : await adminConsoleService.getRoles({ page, limit: PAGE_SIZE, search });
        if (mounted) { setListData(data as PaginationResult<ListItem>); setTrashData(null); }
      } catch (err: any) {
        if (mounted) setError(err.response?.data?.message || "Data could not be loaded.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void run();
    return () => { mounted = false; };
  }, [currentUser, page, search, tab]);

  const refreshCurrentData = async () => {
    try {
      setLoading(true);
      setError("");

      if (page !== 1) {
        setPage(1);
        return;
      }

      if (tab === "trash") {
        const data = await adminConsoleService.getTrash({ page: 1, limit: PAGE_SIZE, search });
        setTrashData(data);
        setListData(null);
        return;
      }

      const data =
        tab === "users" ? await adminConsoleService.getUsers({ page: 1, limit: PAGE_SIZE, search })
          : tab === "admins" ? await adminConsoleService.getAdmins({ page: 1, limit: PAGE_SIZE, search })
            : tab === "domains" ? await adminConsoleService.getDomains({ page: 1, limit: PAGE_SIZE, search })
              : await adminConsoleService.getRoles({ page: 1, limit: PAGE_SIZE, search });

      setListData(data as PaginationResult<ListItem>);
      setTrashData(null);
    } catch (err: any) {
      setError(err.response?.data?.message || "Refresh failed.");
    } finally {
      setLoading(false);
    }
  };

  const openRecord = async (id: string, type: ModalMode) => {
    try {
      setLoading(true);
      const data =
        type === "users" ? await adminConsoleService.getUser(id)
          : type === "admins" ? await adminConsoleService.getAdmin(id)
            : type === "domains" ? await adminConsoleService.getDomain(id)
              : await adminConsoleService.getRole(id);
      setSelectedRecord(data);
      setCreatingNew(false);
      setIsModalOpen(true);
      form.setFieldsValue(normalizeRecordToForm(type, data, meta));
    } catch (err: any) {
      setError(err.response?.data?.message || "Detail could not be loaded.");
    } finally {
      setLoading(false);
    }
  };

  const startCreate = () => {
    if (tab === "trash") return;
    setSelectedRecord(null);
    setCreatingNew(true);
    setIsModalOpen(true);
    form.setFieldsValue(buildEmptyForm(tab as ModalMode, meta));
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCreatingNew(false);
    setSelectedRecord(null);
    form.resetFields();
  };

  const closeProfileModal = () => setProfileOpen(false);

  const syncCachedUser = (nextProfile: ProfileResponse) => {
    const cachedUser = authService.getUser();
    if (!cachedUser) return;
    const nextUser: User = {
      ...cachedUser,
      username: nextProfile.login,
      fullName: nextProfile.name,
      email: nextProfile.email,
      phone: nextProfile.phone,
      image: nextProfile.image,
    };
    setCurrentUser(nextUser);
    authService.saveUser(nextUser);
  };

  const openProfileModal = async () => {
    try {
      setProfileLoading(true);
      const response = await authService.getProfile();
      setProfile(response);
      profileForm.setFieldsValue({
        name: response.name,
        login: response.login,
        email: response.email,
        phone: response.phone,
        image: response.image,
      });
      setProfileOpen(true);
    } catch (err: any) {
      message.error(err.response?.data?.message || "Profile could not be loaded.");
    } finally {
      setProfileLoading(false);
    }
  };

  const handleProfileSave = async () => {
    try {
      const values = await profileForm.validateFields();
      setProfileSaving(true);
      const response = await authService.updateProfile({
        name: values.name,
        login: values.login,
        email: values.email || undefined,
        phone: values.phone || undefined,
      });
      setProfile(response);
      profileForm.setFieldsValue({ ...values, image: response.image });
      syncCachedUser(response);
      message.success("Profile updated successfully.");
      setProfileOpen(false);
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(err.response?.data?.message || "Profile update failed.");
    } finally {
      setProfileSaving(false);
    }
  };

  const handleProfileImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setProfileSaving(true);
      const response = await authService.updateProfileImage(file);
      profileForm.setFieldValue("image", response.image);
      if (profile) {
        const nextProfile = { ...profile, image: response.image };
        setProfile(nextProfile);
        syncCachedUser(nextProfile);
      }
      message.success("Profile image updated successfully.");
    } catch (err: any) {
      message.error(err.response?.data?.message || "Image upload failed.");
    } finally {
      setProfileSaving(false);
      event.target.value = "";
    }
  };

  const handleSave = async () => {
    if (tab === "trash") return;
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      setError("");
      const payload = buildPayloadFromForm(tab as ModalMode, values);
      if (tab === "users") {
        creatingNew || !selectedRecord?.id
          ? await adminConsoleService.createUser(payload)
          : await adminConsoleService.updateUser(selectedRecord.id, payload);
      } else if (tab === "admins") {
        creatingNew || !selectedRecord?.id
          ? await adminConsoleService.createAdmin(payload)
          : await adminConsoleService.updateAdmin(selectedRecord.id, payload);
      } else if (tab === "domains") {
        creatingNew || !selectedRecord?.id
          ? await adminConsoleService.createDomain(payload)
          : await adminConsoleService.updateDomain(selectedRecord.id, payload);
      } else {
        creatingNew || !selectedRecord?.id
          ? await adminConsoleService.createRole(payload)
          : await adminConsoleService.updateRole(selectedRecord.id, payload);
      }
      message.success("Saved successfully.");
      closeModal();
      await refreshCurrentData();
    } catch (err: any) {
      if (err?.errorFields) return;
      const errorMessage = err.response?.data?.message || err.message || "Save failed.";
      message.error(errorMessage);
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSoftDelete = async (
    type: "users" | "admins" | "domains" | "roles",
    id: string,
  ) => {
    try {
      setSubmitting(true);
      if (type === "users") await adminConsoleService.deleteUser(id);
      if (type === "admins") await adminConsoleService.deleteAdmin(id);
      if (type === "domains") await adminConsoleService.deleteDomain(id);
      if (type === "roles") await adminConsoleService.deleteRole(id);
      message.success("Deleted successfully.");
      if (isModalOpen) closeModal();
      await refreshCurrentData();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Delete failed.";
      message.error(errorMessage);
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRestore = async (
    type: "users" | "admins" | "domains" | "roles",
    id: string,
  ) => {
    try {
      setSubmitting(true);
      if (type === "users") await adminConsoleService.restoreUser(id);
      if (type === "admins") await adminConsoleService.restoreAdmin(id);
      if (type === "domains") await adminConsoleService.restoreDomain(id);
      if (type === "roles") await adminConsoleService.restoreRole(id);
      message.success("Restored successfully.");
      await refreshCurrentData();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Restore failed.";
      message.error(errorMessage);
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePermanentDelete = async (
    type: "users" | "admins" | "domains" | "roles",
    id: string,
  ) => {
    try {
      setSubmitting(true);
      if (type === "users") await adminConsoleService.permanentlyDeleteUser(id);
      if (type === "admins") await adminConsoleService.permanentlyDeleteAdmin(id);
      if (type === "domains") await adminConsoleService.permanentlyDeleteDomain(id);
      if (type === "roles") await adminConsoleService.permanentlyDeleteRole(id);
      message.success("Permanently deleted.");
      await refreshCurrentData();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Permanent delete failed.";
      message.error(errorMessage);
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return {
    form,
    profileForm,
    currentUser,
    meta,
    tab,
    page,
    search,
    loading,
    submitting,
    listData,
    trashData,
    selectedRecord,
    error,
    creatingNew,
    isModalOpen,
    profileOpen,
    profileLoading,
    profileSaving,
    profile,
    activeLabel,
    domainOptions,
    backendRoleOptions,
    trashRows,
    setTab,
    setPage,
    setSearch,
    openRecord,
    startCreate,
    closeModal,
    closeProfileModal,
    openProfileModal,
    handleProfileSave,
    handleProfileImageChange,
    refreshCurrentData,
    handleSave,
    handleSoftDelete,
    handleRestore,
    handlePermanentDelete,
  };
}
